import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Size configuration for icons
 */
const SIZE_MAP = {
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-8 w-8",
  xl: "h-12 w-12",
} as const;

const SIZE_VALUE_MAP = {
  sm: 16,
  md: 24,
  lg: 32,
  xl: 48,
} as const;

/**
 * Default underlay sizes based on icon size
 * Provides good proportions for the circular background
 */
const UNDERLAY_SIZE_MAP = {
  sm: 24, // 24px (6 * 4)
  md: 40, // 40px (~6.67 * 6)
  lg: 56, // 56px (7 * 8)
  xl: 80, // 80px (~6.67 * 12)
} as const;

/**
 * Color variant mappings for icons
 */
const VARIANT_COLORS = {
  default: "text-current",
  primary: "text-cede-primary",
  secondary: "text-cede-text-secondary",
  error: "text-cede-error",
  success: "text-cede-success",
  warning: "text-cede-warning",
} as const;

export interface BaseIconProps extends Omit<React.SVGProps<SVGSVGElement>, "onClick"> {
  /**
   * Icon size - can be a preset string or custom number (in pixels)
   */
  "size"?: "sm" | "md" | "lg" | "xl" | number;

  /**
   * Color variant for the icon
   * @default "default"
   */
  "variant"?: keyof typeof VARIANT_COLORS;

  /**
   * Whether to render the icon with a circular background underlay
   */
  "withUnderlay"?: boolean;

  /**
   * Additional Tailwind classes for the underlay background
   * Only applies when withUnderlay is true
   */
  "underlayClassName"?: string;

  /**
   * Custom underlay size in pixels (width and height)
   * If not provided, automatically calculated based on icon size
   */
  "underlaySize"?: number;

  /**
   * Enable interactive mode with hover effects and focus states
   * Makes the icon cursor-pointer and adds hover color transition
   */
  "interactive"?: boolean;

  /**
   * Active state styling (e.g., for selected/active items)
   */
  "active"?: boolean;

  /**
   * Disabled state styling
   */
  "disabled"?: boolean;

  /**
   * Show notification badge (number for count, true for dot)
   */
  "badge"?: number | boolean;

  /**
   * Badge background color (Tailwind class)
   * @default "bg-cede-error"
   */
  "badgeColor"?: string;

  /**
   * Click handler - automatically enables interactive mode
   */
  "onClick"?: (e: React.MouseEvent<HTMLDivElement>) => void;

  /**
   * Hover state change handler
   */
  "onHoverChange"?: (isHovered: boolean) => void;

  /**
   * Accessible label for the icon
   */
  "aria-label"?: string;
}

/**
 * Enhanced base icon component with interactive states, variants, and badge support
 *
 * @example
 * ```tsx
 * // Simple icon
 * <BaseIcon size="md" aria-label="Home">
 *   <path d="..." />
 * </BaseIcon>
 *
 * // Interactive icon with hover
 * <BaseIcon
 *   size="lg"
 *   interactive
 *   variant="primary"
 *   onClick={() => console.log("clicked")}
 * >
 *   <path d="..." />
 * </BaseIcon>
 *
 * // Icon with badge
 * <BaseIcon size="md" badge={5} badgeColor="bg-red-500">
 *   <path d="..." />
 * </BaseIcon>
 *
 * // Icon with underlay and active state
 * <BaseIcon
 *   size="lg"
 *   withUnderlay
 *   underlayClassName="bg-discord"
 *   active
 *   interactive
 * >
 *   <path d="..." />
 * </BaseIcon>
 * ```
 */
export const BaseIcon = React.forwardRef<SVGSVGElement, BaseIconProps>(
  (
    {
      size = "md",
      variant = "default",
      withUnderlay = false,
      underlayClassName,
      underlaySize,
      interactive = false,
      active = false,
      disabled = false,
      badge,
      badgeColor = "bg-cede-error",
      onClick,
      onHoverChange,
      className,
      children,
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) => {
    // Auto-enable interactive if onClick is provided
    const isInteractive = interactive || !!onClick;

    // Determine the icon size class or style
    const iconSizeClass = typeof size === "string" ? SIZE_MAP[size] : undefined;
    const numericSize =
      typeof size === "number"
        ? size
        : typeof size === "string" && !Number.isNaN(Number(size))
          ? Number(size)
          : typeof size === "string" && SIZE_VALUE_MAP[size]
            ? SIZE_VALUE_MAP[size]
            : undefined;
    const iconSizeStyle = numericSize ? { width: numericSize, height: numericSize } : undefined;

    // Calculate underlay size if not provided
    const fallbackUnderlaySize =
      typeof size === "string" && UNDERLAY_SIZE_MAP[size]
        ? UNDERLAY_SIZE_MAP[size]
        : (numericSize ?? UNDERLAY_SIZE_MAP.md) * 1.67;
    const computedUnderlaySize = underlaySize ?? fallbackUnderlaySize;

    // Handle hover state changes
    const handleMouseEnter = () => {
      if (isInteractive && !disabled) {
        onHoverChange?.(true);
      }
    };

    const handleMouseLeave = () => {
      if (isInteractive && !disabled) {
        onHoverChange?.(false);
      }
    };

    // Handle click
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!disabled && onClick) {
        onClick(e);
      }
    };

    // Build icon classes
    const iconClasses = cn(
      iconSizeClass,
      VARIANT_COLORS[variant],
      isInteractive && "transition-colors duration-200",
      isInteractive && !disabled && "cursor-pointer hover:text-cede-primary",
      isInteractive &&
        !disabled &&
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cede-primary",
      active && "text-cede-primary",
      disabled && "opacity-50 cursor-not-allowed",
      className,
    );

    const iconElement = (
      <svg
        ref={ref}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={iconClasses}
        style={iconSizeStyle}
        width={numericSize}
        height={numericSize}
        aria-label={ariaLabel}
        role={onClick ? "button" : ariaLabel ? "img" : undefined}
        aria-hidden={!ariaLabel}
        tabIndex={onClick && !disabled ? 0 : undefined}
        onKeyDown={(e) => {
          if (onClick && !disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onClick(e as unknown as React.MouseEvent<HTMLDivElement>);
          }
        }}
        {...props}
      >
        {children}
      </svg>
    );

    // Wrap with click handler div if needed
    const iconWithHandler = onClick ? (
      <div
        className="inline-flex"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {iconElement}
      </div>
    ) : (
      <div className="inline-flex" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {iconElement}
      </div>
    );

    // Wrap with underlay if needed
    const iconWithUnderlay = withUnderlay ? (
      <div
        className={cn(
          "rounded-full flex items-center justify-center shrink-0",
          "bg-cede-primary/10", // Default underlay color
          isInteractive && !disabled && "transition-all duration-200",
          underlayClassName,
        )}
        style={{
          width: computedUnderlaySize,
          height: computedUnderlaySize,
        }}
      >
        {iconWithHandler}
      </div>
    ) : (
      iconWithHandler
    );

    // Add badge if needed
    if (badge) {
      return (
        <div className="relative inline-flex">
          {iconWithUnderlay}
          <span
            className={cn(
              "absolute -top-1 -right-1 flex items-center justify-center",
              "rounded-full text-xs font-medium text-white",
              typeof badge === "number" ? "min-w-5 h-5 px-1" : "w-2 h-2",
              badgeColor,
            )}
            aria-label={typeof badge === "number" ? `${badge} notifications` : "Has notifications"}
          >
            {typeof badge === "number" && (badge > 99 ? "99+" : badge)}
          </span>
        </div>
      );
    }

    return iconWithUnderlay;
  },
);

BaseIcon.displayName = "BaseIcon";
